import ChalkySticks from '@chalkysticks/sdk';
import Router from '@/Router';
import Store from '@/Store';

/**
 * Handles global action events dispatched from ActionPanel.
 * Listens for 'action:global' events and routes to appropriate handlers.
 *
 * Actions:
 *
 *   - submit: Navigate to venue submission flow
 *   - checkin: Navigate to check-in flow (requires auth)
 *   - addphoto: Dispatch media upload request event
 *   - addvideo: Dispatch media upload request event
 *
 * @author Matt Kenefick <matt@chalkysticks.com>
 * @package Manager/App
 * @project ChalkySticks App
 */

/**
 * Whether the action manager has been initialized
 *
 * @type boolean
 */
let hasInitialized: boolean = false;

/**
 * Initialize action event listeners.
 * Sets up handler for action:global events from ActionPanel.
 *
 * @return void
 */
export function initialize(): void {
	if (hasInitialized) {
		return;
	}

	ChalkySticks.Core.Event.Bus.on('action:global', Handle_OnActionGlobal);

	hasInitialized = true;
	ChalkySticks.Utility.Log.info('app', 'Action manager initialized');
}

/**
 * Handle global action events from ActionPanel.
 *
 * @param ChalkySticks.Core.IDispatcherEvent e
 * @return Promise<void>
 */
async function Handle_OnActionGlobal(e: ChalkySticks.Core.IDispatcherEvent<{ action: string }>): Promise<void> {
	const action = e.data.action;

	ChalkySticks.Utility.Log.info('app', 'Global action received:', action);

	// Close the action panel
	Store.commit('setShowPrimaryContextMenu', false);

	switch (action) {
		case 'post':
		case 'addphoto':
		case 'addvideo':
			await handleAddPhoto();
			break;

		case 'checkin':
			await handleCheckin();
			break;

		case 'submit':
			await handleSubmit();
			break;

		case 'create':
			await handleCreate();
			break;

		case 'play':
			await handlePlay();
			break;

		default:
			ChalkySticks.Utility.Log.warn('app', 'Unknown global action:', action);
	}
}

/**
 * Handle submit action - navigate to venue submission.
 *
 * @return Promise<void>
 */
// async function handleSubmit(): Promise<void> {
// 	Router.push({
// 		name: 'PagePlaces',
// 		query: {
// 			tab: 'submit',
// 		},
// 	});
// }

/**
 * Handle submit modal action - open venue submission modal.
 *
 * @return Promise<void>
 */
async function handleSubmit(): Promise<void> {
	// Check if user is authenticated
	if (!Store.getters['authentication/authenticated']) {
		ChalkySticks.Core.Event.Bus.dispatch('login:request');
		return;
	}

	// Open submit venue modal
	ChalkySticks.Core.Event.Bus.dispatch('modal:submit:open');
}

/**
 * Handle checkin action - open checkin modal.
 *
 * @return Promise<void>
 */
async function handleCheckin(): Promise<void> {
	// Check if user is authenticated
	if (!Store.getters['authentication/authenticated']) {
		ChalkySticks.Core.Event.Bus.dispatch('login:request');
		return;
	}

	// Open checkin modal
	ChalkySticks.Core.Event.Bus.dispatch('modal:checkin:open');
}

/**
 * Handle create action - open pad creator.
 *
 * @return Promise<void>
 */
async function handleCreate(): Promise<void> {
	// Check if user is authenticated
	if (!Store.getters['authentication/authenticated']) {
		ChalkySticks.Core.Event.Bus.dispatch('login:request');
		return;
	}

	// Open pad creator modal
	// ChalkySticks.Core.Event.Bus.dispatch('modal:create:open');
	Router.push({
		name: 'PageCreate',
	});
}

/**
 * Handle add photo action - open photo upload modal.
 *
 * @return Promise<void>
 */
async function handleAddPhoto(): Promise<void> {
	// Check if user is authenticated
	if (!Store.getters['authentication/authenticated']) {
		ChalkySticks.Core.Event.Bus.dispatch('login:request');
		return;
	}

	// Open photo upload modal
	ChalkySticks.Core.Event.Bus.dispatch('modal:photo:open');
}

/**
 * Handle play action - open game
 *
 * @return Promise<void>
 */
async function handlePlay(): Promise<void> {
	// Check if user is authenticated
	if (!Store.getters['authentication/authenticated']) {
		ChalkySticks.Core.Event.Bus.dispatch('login:request');
		return;
	}

	// Open pad creator modal
	// ChalkySticks.Core.Event.Bus.dispatch('modal:play:open');
	Router.push({
		name: 'PagePlay',
	});
}

/**
 * Handle add video action - open video upload modal.
 *
 * @return Promise<void>
 */
async function handleAddVideo(): Promise<void> {
	// Check if user is authenticated
	if (!Store.getters['authentication/authenticated']) {
		ChalkySticks.Core.Event.Bus.dispatch('login:request');
		return;
	}

	// Open video upload modal
	ChalkySticks.Core.Event.Bus.dispatch('modal:video:open');
}
